import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import PageHeader from "@/components/feature/PageHeader";
import Pagination from "@/components/feature/Pagination";
import EmptyState from "@/components/base/EmptyState";
import CardSkeleton from "@/components/base/CardSkeleton";
import FilterChips from "@/components/base/FilterChips";
import NewsCard from "@/components/cards/NewsCard";
import { newsList } from "@/mocks/news";
import { useSeo } from "@/hooks/useSeo";

const PAGE_SIZE = 6;

const sortOptions = [
  { label: "最新发布", value: "desc" },
  { label: "最早发布", value: "asc" },
];

export default function NewsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [keyword, setKeyword] = useState(searchParams.get("q") ?? "");
  const [sort, setSort] = useState("desc");
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);

  useSeo({
    title: "最新电影资讯 - 蜜桃成熟时3D",
    description:
      "蜜桃成熟时3D最新电影资讯合集，涵盖影片动态、幕后访谈、影展资讯与口碑评价，支持关键词搜索与按时间排序。",
    keywords: "蜜桃成熟时3D资讯,电影新闻,影片动态,影展资讯,幕后访谈",
    path: "/news",
  });

  useEffect(() => {
    setLoading(true);
    const timer = window.setTimeout(() => setLoading(false), 350);
    return () => window.clearTimeout(timer);
  }, [keyword, sort, page]);

  const filtered = useMemo(() => {
    const key = keyword.trim().toLowerCase();
    const list = newsList.filter((item) => {
      if (!key) return true;
      return (
        item.title.toLowerCase().includes(key) ||
        item.summary.toLowerCase().includes(key) ||
        item.content.toLowerCase().includes(key) ||
        item.category.toLowerCase().includes(key) ||
        item.tags.some((tag) => tag.toLowerCase().includes(key))
      );
    });
    return list.sort((a, b) =>
      sort === "desc" ? b.date.localeCompare(a.date) : a.date.localeCompare(b.date),
    );
  }, [keyword, sort]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const paged = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  function handleKeyword(value: string) {
    setKeyword(value);
    setPage(1);
    const next = new URLSearchParams(searchParams);
    if (value.trim()) next.set("q", value.trim());
    else next.delete("q");
    setSearchParams(next, { replace: true });
  }

  return (
    <>
      <PageHeader
        eyebrow="NEWS"
        title="最新电影资讯"
        description="汇总「蜜桃成熟时3D」的影片动态、幕后访谈、影展资讯与口碑评价。输入关键词即可检索相关新闻，也可按发布时间排序浏览。"
        crumbs={[{ label: "新闻资讯" }]}
      >
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex items-center gap-2 rounded-md border border-background-300 bg-background-50 px-3 py-2 lg:w-80">
            <i className="ri-search-line w-4 h-4 flex items-center justify-center text-foreground-400"></i>
            <input
              value={keyword}
              onChange={(e) => handleKeyword(e.target.value)}
              placeholder="搜索新闻标题或关键词"
              className="w-full bg-transparent text-sm outline-none placeholder:text-foreground-400"
            />
            {keyword && (
              <button type="button" onClick={() => handleKeyword("")} aria-label="清空搜索" className="text-foreground-400 hover:text-foreground-700">
                <i className="ri-close-circle-line w-4 h-4 flex items-center justify-center"></i>
              </button>
            )}
          </div>
          <FilterChips options={sortOptions} value={sort} onChange={(v) => { setSort(v); setPage(1); }} label="排序" />
        </div>
      </PageHeader>

      <section className="bg-background-50 py-10 md:py-14">
        <div className="mx-auto max-w-[1200px] px-4 md:px-6">
          <p className="mb-6 text-sm text-foreground-500">
            共找到 <span className="font-medium text-primary-700">{filtered.length}</span> 条相关资讯
            {keyword && <span> · 关键词「{keyword}」</span>}
          </p>

          {loading ? (
            <CardSkeleton count={6} />
          ) : paged.length > 0 ? (
            <>
              <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
                {paged.map((item) => (
                  <NewsCard key={item.id} item={item} />
                ))}
              </div>
              <div className="mt-10">
                <Pagination current={currentPage} total={totalPages} onChange={setPage} />
              </div>
            </>
          ) : (
            <EmptyState
              icon="ri-file-search-line"
              title="没有找到相关资讯"
              description="换一个关键词试试，或者先返回首页浏览全部内容。"
              actionLabel="返回首页"
              actionTo="/"
            />
          )}
        </div>
      </section>
    </>
  );
}